import { formatShare } from "@nt/ui/breakdown-list";
import { chartColorVar } from "@nt/ui/charts";
import { buttonVariants } from "@nt/ui/recipes";
import { EmptyState } from "@nt/ui/states";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableStack,
  TableStackField,
  TableStackFields,
  TableStackRow,
  TableStackTitle,
} from "@nt/ui/table";
import { Link } from "react-router";

import { Section } from "@/components/section";
import { money } from "@/lib/format";
import type { PerformanceData } from "@/lib/types";
import {
  count,
  countryName,
  ordersPer100Clicks,
} from "@/pages/performance/format";

export type Report = "links" | "referrers" | "countries" | "pages";

interface Row {
  key: string;
  label: string;
  /** A second line under the label (a custom link's tag). */
  sub?: string | null;
  clicks: number;
  signups: number;
  firstOrders: number;
  earnedCents?: number;
}

const REPORTS: Record<
  Report,
  {
    title: string;
    description: string;
    column: string;
    mono?: boolean;
    empty: { title: string; description: string };
    rows: (data: PerformanceData) => Row[];
  }
> = {
  links: {
    title: "By link",
    description: "Your main link and each custom link.",
    column: "Link",
    empty: {
      title: "No link clicks in this range",
      description:
        "Share your link, or make a custom link for each place you post it, to see which one works best.",
    },
    rows: (d) =>
      d.byLink.map((r) => ({
        key: r.key,
        label: r.name,
        sub: r.tag,
        clicks: r.clicks,
        signups: r.signups,
        firstOrders: r.firstOrders,
        earnedCents: r.earnedCents,
      })),
  },
  referrers: {
    title: "By referrer",
    description: "The site people were on before they clicked your link.",
    column: "Referrer",
    empty: {
      title: "No referring sites in this range",
      description:
        "Share your link on a website or in a post. The sites people come from show here.",
    },
    rows: (d) =>
      d.byReferrer.map((r) => ({ key: r.host, label: r.host, ...r })),
  },
  countries: {
    title: "By country",
    description: "Where people were when they clicked your link.",
    column: "Country",
    empty: {
      title: "No countries in this range",
      description: "Share your link to see where people click from.",
    },
    rows: (d) =>
      d.byCountry.map((r) => ({
        key: r.country,
        label: countryName(r.country),
        ...r,
      })),
  },
  pages: {
    title: "By page",
    description: "The page each click landed on.",
    column: "Page",
    mono: true,
    empty: {
      title: "No landing pages in this range",
      description:
        "Share your link, or a custom link to a page like Pricing, to see which pages people land on.",
    },
    rows: (d) =>
      d.byLandingPage.map((r) => ({ key: r.path, label: r.path, ...r })),
  },
};

const num = "text-right tabular-nums";

/** The share of all clicks in the range as a thin bar and a percent. */
function ShareBar({ pct, wide = false }: { pct: number; wide?: boolean }) {
  const width = Math.min(100, Math.max(0, pct));
  return (
    <span
      className={
        wide ? "flex items-center gap-2" : "flex items-center justify-end gap-2"
      }
    >
      <span
        aria-hidden="true"
        className={
          wide
            ? "block h-1.5 flex-1 overflow-hidden rounded-pill bg-muted"
            : "block h-1.5 w-16 overflow-hidden rounded-pill bg-muted"
        }
      >
        <span
          data-slot="share-fill"
          className="block h-full rounded-pill"
          style={{ width: `${width}%`, background: chartColorVar("chart-1") }}
        />
      </span>
      <span className="w-9 text-right tabular-nums">{formatShare(pct)}</span>
    </span>
  );
}

/**
 * One report tab: a table on a desktop, a stack of rows on a phone, each row
 * with its share of the range's clicks and its orders per 100 clicks.
 */
export function ReportTable({
  data,
  report,
}: {
  data: PerformanceData;
  report: Report;
}) {
  const def = REPORTS[report];
  const rows = def.rows(data);
  const withFees = report === "links";
  const share = (clicks: number) =>
    data.totals.clicks > 0 ? (clicks / data.totals.clicks) * 100 : 0;
  const range =
    data.days === 365 ? "Last 12 months." : `Last ${data.days} days.`;

  return (
    <Section
      title={def.title}
      description={`${def.description} ${range}`}
      contentClassName={rows.length === 0 ? undefined : "px-0"}
    >
      {rows.length === 0 ? (
        <EmptyState
          title={def.empty.title}
          description={def.empty.description}
          action={
            <Link
              to="/links"
              className={buttonVariants({ variant: "secondary", size: "sm" })}
            >
              Get your links
            </Link>
          }
        />
      ) : (
        <>
          <div className="hidden sm:block">
            <Table density="compact">
              <TableHeader>
                <TableRow>
                  <TableHead>{def.column}</TableHead>
                  <TableHead className={num}>Clicks</TableHead>
                  <TableHead className={num}>Share of clicks</TableHead>
                  <TableHead className={num}>Sign-ups</TableHead>
                  <TableHead className={num}>First orders</TableHead>
                  <TableHead className={num}>Orders per 100 clicks</TableHead>
                  {withFees ? (
                    <TableHead className={num}>Fees earned</TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.key}>
                    <TableCell className="max-w-72 normal-nums">
                      <div
                        className={
                          def.mono
                            ? "truncate font-mono text-compact"
                            : "truncate font-medium"
                        }
                        title={r.label}
                      >
                        {r.label}
                      </div>
                      {r.sub ? (
                        <div className="truncate font-mono text-xs text-muted-foreground">
                          {r.sub}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className={num}>{count(r.clicks)}</TableCell>
                    <TableCell className={num}>
                      <ShareBar pct={share(r.clicks)} />
                    </TableCell>
                    <TableCell className={num}>{count(r.signups)}</TableCell>
                    <TableCell className={num}>
                      {count(r.firstOrders)}
                    </TableCell>
                    <TableCell className={num}>
                      {ordersPer100Clicks(r.firstOrders, r.clicks)}
                    </TableCell>
                    {withFees ? (
                      <TableCell className={num}>
                        {money(r.earnedCents ?? 0)}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <TableStack className="sm:hidden">
            {rows.map((r) => (
              <TableStackRow key={r.key}>
                <TableStackTitle>
                  <span
                    className={
                      def.mono ? "truncate font-mono text-compact" : "truncate"
                    }
                  >
                    {r.label}
                  </span>
                  {r.sub ? (
                    <span className="shrink-0 font-mono text-xs font-normal text-muted-foreground">
                      {r.sub}
                    </span>
                  ) : null}
                </TableStackTitle>
                <ShareBar pct={share(r.clicks)} wide />
                <TableStackFields>
                  <TableStackField label="Clicks">
                    {count(r.clicks)}
                  </TableStackField>
                  <TableStackField label="Sign-ups">
                    {count(r.signups)}
                  </TableStackField>
                  <TableStackField label="First orders">
                    {count(r.firstOrders)}
                  </TableStackField>
                  <TableStackField label="Orders per 100 clicks">
                    {ordersPer100Clicks(r.firstOrders, r.clicks)}
                  </TableStackField>
                  {withFees ? (
                    <TableStackField label="Fees earned">
                      {money(r.earnedCents ?? 0)}
                    </TableStackField>
                  ) : null}
                </TableStackFields>
              </TableStackRow>
            ))}
          </TableStack>
        </>
      )}
    </Section>
  );
}
